import { Box, Skeleton, SxProps, Theme, Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import { GiornoFascia, StatoDati } from '../../hooks/usePresenzeGiorni';
import { FasceOrarie } from '../../utility_objects/FasceOrarieUtils';
import {
  ChipPersona,
  formatGiornoCorto,
  getChipPresenze,
  TestoChip,
} from '../../utility_objects/PresenzeUtils';
import { ALTEZZA_SKELETON, CellaCliccabile } from './CellaCliccabile';
import { ChipPresenza } from './ChipPresenza';

// Ogni riga è alta almeno come una chip su una riga.
const ALTEZZA_RIGA = 18;

// Le chip vanno a capo: nessuna resta fuori e non c'è un «+N». In alto a sinistra,
// anche quando una chip su più righe è più alta delle altre.
const stileGruppo: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: 0.5,
  minWidth: 0,
  minHeight: ALTEZZA_RIGA,
};

const stileTesto = { lineHeight: `${ALTEZZA_RIGA}px` } as const;

// Le scritte di stato restano su una riga (coi puntini se la colonna è stretta):
// la cella in caricamento o in errore non supera i 48px. Il testo completo resta
// nel popover e nel nome accessibile della cella.
const stileScritta = { ...stileTesto, minWidth: 0, maxWidth: '100%' } as const;

const ElencoChip = ({ chip }: { chip: ChipPersona[] }): JSX.Element => (
  <>
    {chip.map(({ userId, label, stato, tipo }) => (
      <ChipPresenza key={userId} label={label} stato={stato} tipo={tipo} />
    ))}
  </>
);

const TestoStato = ({ testo }: { testo: string }): JSX.Element => (
  <Typography variant="caption" color="text.secondary" noWrap sx={stileScritta}>
    {testo}
  </Typography>
);

// Prima riga (che va a capo): chi è in servizio, anche solo in parte, per ora
// d'inizio e username, compreso chi è assente in parte e non compare nella risposta
// «in servizio» (getChipPresenze). Nell'Agenda inizia con la data.
const GruppoInServizio = ({
  giorno,
  chip,
  conData,
}: {
  giorno: GiornoFascia;
  chip: ChipPersona[];
  conData: boolean;
}): JSX.Element => (
  <Box sx={stileGruppo}>
    {conData && (
      <Typography
        variant="caption"
        noWrap
        sx={{ ...stileTesto, flexShrink: 0 }}
      >
        {formatGiornoCorto(giorno.giorno)}
      </Typography>
    )}
    {giorno.statoInServizio === 'caricamento' && (
      <Skeleton
        variant="rounded"
        width="70%"
        height={ALTEZZA_SKELETON}
        sx={{ mt: 0.375 }}
      />
    )}
    {giorno.statoInServizio === 'errore' && (
      <TestoStato testo="Non disponibile" />
    )}
    {/* Va a capo come il testo delle chip: niente puntini, come in FOCUS CLOUD */}
    {giorno.inServizio !== null && chip.length === 0 && (
      <Typography variant="caption" color="text.secondary" sx={stileTesto}>
        Nessuno in servizio
      </Typography>
    )}
    <ElencoChip chip={chip} />
  </Box>
);

// Sotto, dalla riga dopo: gli assenti di tutto il giorno per gravità e username.
const GruppoAssenti = ({
  giorno,
  chip,
  statoAssenze,
}: {
  giorno: GiornoFascia;
  chip: ChipPersona[];
  statoAssenze: StatoDati;
}): JSX.Element | null => {
  if (!giorno.assenti)
    return (
      <Box sx={stileGruppo}>
        {statoAssenze === 'caricamento' ? (
          <Skeleton
            variant="rounded"
            width="50%"
            height={ALTEZZA_SKELETON}
            sx={{ mt: 0.375 }}
          />
        ) : (
          <TestoStato testo="Assenze non disponibili" />
        )}
      </Box>
    );
  if (chip.length === 0) return null;
  return (
    <Box sx={stileGruppo}>
      <ElencoChip chip={chip} />
    </Box>
  );
};

interface CellaPresenzeProps {
  giorno: GiornoFascia;
  statoAssenze: StatoDati;
  /**
   * Testo delle chip: «Presenze» il solo username, «Presenze totali» il testo
   * completo (formatChipPresenza).
   */
  testo: TestoChip;
  /**
   * Fasce del punto vendita nel giorno, per «Orario negozio» nel testo completo;
   * null = non disponibili (si mostra l'orario).
   */
  orarioNegozio: FasceOrarie | null;
  /** Agenda: la cella inizia con la data corta. */
  conData: boolean;
  /** Divisoria a sinistra, come fra le colonne dell'intestazione. */
  bordo: boolean;
}

/**
 * Cella di un giorno della fascia Presenze, uguale nelle due modalità: una chip per
 * persona, sempre tutte, in alto a sinistra e che vanno a capo (prima chi è in
 * servizio, anche solo in parte, poi gli assenti di tutto il giorno); lo stato si
 * legge dal colore. Cambia solo il testo delle chip (`testo`), uguale in ogni vista.
 * Alta almeno 48px, cresce quanto serve. Tutta la cella apre il popover col
 * dettaglio.
 */
export const CellaPresenze = memo(function CellaPresenze({
  giorno,
  statoAssenze,
  testo,
  orarioNegozio,
  conData,
  bordo,
}: CellaPresenzeProps): JSX.Element {
  const chip = useMemo(
    () => getChipPresenze(giorno, testo, orarioNegozio),
    [giorno, testo, orarioNegozio]
  );
  return (
    <CellaCliccabile giorno={giorno} statoAssenze={statoAssenze} bordo={bordo}>
      <GruppoInServizio
        giorno={giorno}
        chip={chip.inServizio}
        conData={conData}
      />
      <GruppoAssenti
        giorno={giorno}
        chip={chip.assenti}
        statoAssenze={statoAssenze}
      />
    </CellaCliccabile>
  );
});
